import React, {
  useState,
  useMemo,
  useEffect,
  useRef,
  useCallback,
} from "react";
import ReusableTable from "../components/Table/ReusableTable";
import { ArrowBigLeft, Eye, Search, Download, Calendar as CalendarIcon, CheckCircle2 } from "lucide-react";
import Button from "../components/Button/Button";
import { InputText } from "primereact/inputtext";
import { InputTextarea } from "primereact/inputtextarea";
import { Dropdown } from "primereact/dropdown";
import ReactCalendar from "react-calendar";
import "react-calendar/dist/Calendar.css";
import { get, post } from "../utils/api";
import { toast, ToastContainer } from "react-toastify";
import moment from "moment";
import CustomDialog from "../components/dialog/CustomDialog";
import { useNavigate } from "react-router-dom";

// Add this function to format numbers with Indian comma separators
const formatIndianNumber = (num) => {
  if (isNaN(num)) return "0";

  // Convert to string and split into integer and decimal parts
  const numStr = parseFloat(num).toFixed(2);
  const [integerPart, decimalPart] = numStr.split(".");

  // Format integer part with Indian numbering system
  let lastThree = integerPart.substring(integerPart.length - 3);
  let otherNumbers = integerPart.substring(0, integerPart.length - 3);

  if (otherNumbers !== "") {
    lastThree = "," + lastThree;
  }

  let formatted =
    otherNumbers.replace(/\B(?=(\d{2})+(?!\d))/g, ",") + lastThree;

  // Add decimal part if it exists and is not .00
  if (decimalPart && decimalPart !== "00") {
    formatted += "." + decimalPart;
  }

  return formatted;
};

const numberToWords = (num) => {
  if (isNaN(num) || num === 0) return "Zero Rupees";

  let remainingValue = Math.floor(Math.abs(num));
  const isNegative = num < 0;

  const ones = [
    "", "One", "Two", "Three", "Four", "Five", "Six", "Seven", "Eight", "Nine",
    "Ten", "Eleven", "Twelve", "Thirteen", "Fourteen", "Fifteen", "Sixteen",
    "Seventeen", "Eighteen", "Nineteen"
  ];

  const tens = [
    "", "", "Twenty", "Thirty", "Forty", "Fifty", "Sixty", "Seventy", "Eighty", "Ninety"
  ];

  let words = "";

  // Crore part
  if (remainingValue >= 10000000) {
    words += numberToWords(Math.floor(remainingValue / 10000000)).replace(" Rupees", "") + " Crore ";
    remainingValue %= 10000000;
  }

  // Lakh part
  if (remainingValue >= 100000) {
    words += numberToWords(Math.floor(remainingValue / 100000)).replace(" Rupees", "") + " Lakh ";
    remainingValue %= 100000;
  }

  // Thousand part
  if (remainingValue >= 1000) {
    words += numberToWords(Math.floor(remainingValue / 1000)).replace(" Rupees", "") + " Thousand ";
    remainingValue %= 1000;
  }

  // Hundred part
  if (remainingValue >= 100) {
    words += numberToWords(Math.floor(remainingValue / 100)).replace(" Rupees", "") + " Hundred ";
    remainingValue %= 100;
  }

  // Tens and ones part
  if (remainingValue > 0) {
    if (words !== "") words += "and ";

    if (remainingValue < 20) {
      words += ones[remainingValue];
    } else {
      words += tens[Math.floor(remainingValue / 10)];
      if (remainingValue % 10 > 0) {
        words += " " + ones[remainingValue % 10];
      }
    }
  }

  return (isNegative ? "Minus " : "") + words.trim() + " Rupees";
};

const Invoice = () => {
  const navigate = useNavigate();
  // States for view control
  const [view, setView] = useState("list");
  const [statusFilter, setStatusFilter] = useState("");
  const [selectedInvoice, setSelectedInvoice] = useState(null);
  const [statusOptions, setStatusOptions] = useState([]);
  const [isLoading, setIsLoading] = useState(false);

  console.log(selectedInvoice, "selectedInvoice");

  // State to track if dates should be frozen
  const [datesFrozen, setDatesFrozen] = useState(false);

  // Rejection modal state
  const [rejectModalVisible, setRejectModalVisible] = useState(false);
  const [rejectionRemark, setRejectionRemark] = useState("");

  // Resubmit modal state
  const [resubmitModalVisible, setResubmitModalVisible] = useState(false);
  const [invoiceToResubmit, setInvoiceToResubmit] = useState(null);

  // Calendar states
  const [fromDate, setFromDate] = useState(null);
  const [toDate, setToDate] = useState(null);
  const [fromCalendarOpen, setFromCalendarOpen] = useState(false);
  const [toCalendarOpen, setToCalendarOpen] = useState(false);
  const [toDateActiveMonth, setToDateActiveMonth] = useState(new Date()); // Track active month for to date

  // Refs for calendar click outside handling
  const calendarRef = useRef(null);
  const inputRef = useRef(null);
  const toInputRef = useRef(null);

  // Form data state
  const [formData, setFormData] = useState({
    distributorName: "",
    arnCode: "",
    invoiceNumber: "",
    amount: "",
    gst: "",
    remark: "",
    invoiceFile: null,
    calculationFile: null,
    invoiceId: "",
    // Add these for tracking previous files
    previousInvoiceFileName: "",
    previousCalculationFileName: "",
    previousInvoiceFileId: "",
    previousCalculationFileId: "",
  });

  // Main data state
  const [fullData, setFullData] = useState({
    distributor_details: [],
    invoice_details: [],
  });

  // User details state
  const [panNumber, setPanNumber] = useState("");
  const [userRole, setUserRole] = useState("");
  const [userId, setUserId] = useState("");
  const [distributorId, setDistributorId] = useState("");
  const [userDetails, setUserDetails] = useState({});
  const [distributorKycStatus, setDistributorKycStatus] = useState("");

  // Get user details directly from localStorage
  const userDetailsFromStorage = JSON.parse(
    localStorage.getItem("user_details") || "{}"
  );
  const isDistributor = userDetailsFromStorage.role_id === 2;
  const normalizedDistributorKycStatus = String(distributorKycStatus || "")
    .trim()
    .toLowerCase();
  const isDistributorKycReady =
    !isDistributor || normalizedDistributorKycStatus === "submitted";

  // Get user permissions
  const uiDetails = JSON.parse(localStorage.getItem("ui_details") || "[]");
  const invoicePermission = uiDetails.find((item) => item.path === "/invoice");
  const isMaker = invoicePermission?.is_maker == true;
  const isChecker = invoicePermission?.is_checker == true;

  // Fetch status options on mount
  useEffect(() => {
    const fetchStatusOptions = async () => {
      try {
        const response = await get("/invoice/getInvoiceStatusMaster");
        if (response?.data?.Success) {
          setStatusOptions(response.data.Data);
        }
      } catch (error) {
        toast.error("Failed to fetch status options");
        console.error(error);
      }
    };

    fetchStatusOptions();
  }, []);

  // Effect to handle user details and role
  useEffect(() => {
    setUserDetails(userDetailsFromStorage);
    setUserRole(isDistributor ? "distributor" : "admin");
    setUserId(userDetailsFromStorage.id?.toString() || "");
    setDistributorId(userDetailsFromStorage.distributor_id?.toString() || "");

    if (isDistributor) {
      if (!userDetailsFromStorage.pan_number) {
        toast.error("PAN number not found. Please login again.");
        return;
      }
      setPanNumber(userDetailsFromStorage.pan_number);

      setFormData((prev) => ({
        ...prev,
        arnCode: userDetailsFromStorage.arn_code || "",
        distributorName: userDetailsFromStorage.distributor_name || "",
      }));
    }
  }, []);

  useEffect(() => {
    const fetchDistributorKycStatus = async () => {
      if (!isDistributor || !userDetailsFromStorage.email) return;

      try {
        const response = await get(
          `/onboarding/getDistributorByEmail?email=${encodeURIComponent(
            userDetailsFromStorage.email
          )}`
        );

        if (response.data?.Success && response.data.Data) {
          setDistributorKycStatus(response.data.Data.kyc_status || "");
        }
      } catch (error) {
        console.error("Failed to fetch distributor KYC status", error);
      }
    };

    fetchDistributorKycStatus();
  }, [isDistributor, userDetailsFromStorage.email]);

  useEffect(() => {
    if (view === "add" && userRole === "distributor") {
      const distributor = fullData.distributor_details.find(
        (d) => d.pan_number === panNumber
      );

      if (distributor) {
        setFormData((prev) => ({
          ...prev,
          distributorName: distributor.distributor_name || "",
          arnCode: distributor.arn_code || "",
        }));
        setDistributorId(distributor.id || "");
      }
    }
  }, [view, fullData.distributor_details, userRole, panNumber]);

  useEffect(() => {
    const handler = (e) => {
      if (
        calendarRef.current &&
        !calendarRef.current.contains(e.target) &&
        inputRef.current &&
        !inputRef.current.contains(e.target) &&
        toInputRef.current &&
        !toInputRef.current.contains(e.target)
      ) {
        setFromCalendarOpen(false);
        setToCalendarOpen(false);
      }
    };
    document.addEventListener("mousedown", handler);
    return () => document.removeEventListener("mousedown", handler);
  }, []);

  // Function to download files
  const downloadFile = async (fileId, fileName) => {
    try {
      setIsLoading(true);

      // Call API to get file info
      const response = await get(`/invoice/downloadFile/${fileId}`);

      if (!response.data.Success || !response.data.Data?.file_path) {
        toast.error("File not found");
        return;
      }

      const { file_path, file_name } = response.data.Data;

      // 👉 Direct download via link (no fetch, no CORS issue)
      const link = document.createElement("a");
      link.href = file_path;
      link.setAttribute("download", file_name || fileName);
      link.target = "_blank"; // fallback in case "download" is ignored
      document.body.appendChild(link);
      link.click();
      link.remove();
    } catch (error) {
      toast.error("Failed to download file");
      console.error(error);
    } finally {
      setIsLoading(false);
    }
  };

  // Function to handle viewing invoice details
  const handleViewInvoice = useCallback(async (invoiceId) => {
    setIsLoading(true);
    try {
      const response = await post("/invoice/fetchInvoiceById", {
        invoice_id: invoiceId,
      });

      if (response?.data?.Success) {
        const invoiceData = response.data.Data[0];

        // Transform the API response to match your state structure
        const transformedInvoice = {
          id: invoiceData.invoice_details[0]?.id,
          distributorName:
            invoiceData.invoice_details[0]?.distributor_name || "",
          arnCode: invoiceData.invoice_details[0]?.distributor_arn_code || "",
          invoiceNumber: invoiceData.invoice_details[0]?.invoice_number,
          invoiceFromDate: invoiceData.invoice_details[0]?.invoice_from_date,
          invoiceToDate: invoiceData.invoice_details[0]?.invoice_to_date,
          invoiceMonth: invoiceData.invoice_details[0]?.invoice_from_date
            ? new Date(
              invoiceData.invoice_details[0]?.invoice_from_date
            ).toLocaleDateString("en-US", {
              year: "numeric",
              month: "long",
            })
            : "",
          amount: `₹${formatIndianNumber(
            invoiceData.invoice_details[0]?.amount || 0
          )}`,
          gst: `₹${formatIndianNumber(
            invoiceData.invoice_details[0]?.gst || 0
          )}`,
          totalAmount: `₹${formatIndianNumber(
            invoiceData.invoice_details[0]?.total_amount || 0
          )}`,
          status: getStatusText(invoiceData.invoice_details[0]?.status_id),
          statusId: invoiceData.invoice_details[0]?.status_id,
          utrNo: invoiceData.invoice_details[0]?.utr_no || "",
          remark: invoiceData.invoice_details[0]?.remark || "",
          rejectionRemark:
            invoiceData.invoice_details[0]?.rejection_remark || "-",
          // Store file IDs and names for download
          invoiceFileId:
            invoiceData.invoice_file_details?.find(
              (file) => file.file_type === "invoiceFile"
            )?.id || "",
          calculationFileId:
            invoiceData.invoice_file_details?.find(
              (file) => file.file_type === "calculationFile"
            )?.id || "",
          invoiceFileName:
            invoiceData.invoice_file_details?.find(
              (file) => file.file_type === "invoiceFile"
            )?.file_name ||
            `invoice_${invoiceData.invoice_details[0]?.invoice_number}.pdf`,
          calculationFileName:
            invoiceData.invoice_file_details?.find(
              (file) => file.file_type === "calculationFile"
            )?.file_name ||
            `calculation_${invoiceData.invoice_details[0]?.invoice_number}.xlsx`,
          createdDate: new Date(
            invoiceData.invoice_details[0]?.created_date
          ).toLocaleDateString(),
        };

        setSelectedInvoice(transformedInvoice);
        setView("view");
      } else {
        toast.error(
          response?.data?.Message || "Failed to fetch invoice details"
        );
      }
    } catch (error) {
      toast.error("Error occurred while fetching invoice details");
      console.error(error);
    } finally {
      setIsLoading(false);
    }
  }, []);

  // Table columns definition
  const columns = useMemo(() => {
    const baseColumns = [
      { Header: "Distributor Name", accessor: "distributorName", size: 220 },
      { Header: "APRN Code", accessor: "arnCode", size: 120 },
      { Header: "Invoice Number", accessor: "invoiceNumber", size: 160 },
      { Header: "Invoice Month", accessor: "invoiceMonth", size: 150 },
      {
        Header: "Total Amount",
        accessor: "totalAmount",
        size: 160,
        Cell: ({ value }) => (
          <span className="font-bold text-gray-800 tracking-tight">{value}</span>
        )
      },
      {
        Header: "Status",
        accessor: "status",
        size: 160,
        Cell: ({ value }) => {
          const isApproved = value === "APPROVED";
          const isRejected = value === "REJECTED";
          const isPending = value === "PENDING FOR APPROVAL";

          return (
            <div className={`px-3 py-1 rounded-full text-[10px] font-black uppercase tracking-tighter text-center border shadow-sm ${isApproved ? "bg-emerald-50 text-emerald-700 border-emerald-100" :
              isRejected ? "bg-red-50 text-red-700 border-red-100" :
                isPending ? "bg-indigo-50 text-indigo-700 border-indigo-100" :
                  "bg-gray-50 text-gray-600 border-gray-100"
              }`}>
              {value}
            </div>
          );
        }
      },
      { Header: "UTR No", accessor: "utrNo", size: 150 },
      {
        Header: "Action",
        accessor: "action",
        size: 120,
        Cell: ({ row }) => (
          <div className="flex items-center gap-2">
            <button
              title="View Details"
              onClick={() => handleViewInvoice(row.original.id)}
              className="p-2 bg-indigo-50 text-indigo-600 rounded-lg hover:bg-indigo-600 hover:text-white transition-all duration-300 shadow-sm"
            >
              <Eye className="w-4 h-4" />
            </button>
            {isDistributor && row.original.status == "REJECTED" && (
              <button
                title="Re-Edit Invoice"
                onClick={() => handleReEdit(row.original.id)}
                className="p-2 bg-red-50 text-red-600 rounded-lg hover:bg-red-600 hover:text-white transition-all duration-300 shadow-sm"
              >
                <ArrowBigLeft className="w-4 h-4 rotate-180" />
              </button>
            )}
            {isDistributor && row.original.status == "DRAFT" && (
              <button
                title="Edit Invoice"
                onClick={() => handleEdit(row.original.id)}
                className="p-2 bg-amber-50 text-amber-600 rounded-lg hover:bg-amber-600 hover:text-white transition-all duration-300 shadow-sm"
              >
                <Eye className="w-4 h-4" />
              </button>
            )}
          </div>
        ),
      },
    ];

    return baseColumns;
  }, [isMaker, handleViewInvoice, isDistributor]);

  const getStatusText = (statusId) => {
    const statusMap = {
      1: "DRAFT",
      2: "PENDING FOR APPROVAL",
      3: "APPROVED",
      4: "REJECTED",
    };
    return statusMap[statusId] || "Unknown";
  };

  // Filter data based on status
  const filteredData = useMemo(() => {
    return fullData.invoice_details.filter((row) => {
      const matchesStatus = (statusFilter && statusFilter !== 'All Status')
        ? row.status.toLowerCase() === statusFilter.toLowerCase()
        : true;
      return matchesStatus;
    });
  }, [statusFilter, fullData.invoice_details]);

  // Fetch invoice data when PAN changes
  useEffect(() => {
    const fetchInvoiceData = async () => {
      setIsLoading(true);
      try {
        const requestBody =
          userRole === "distributor"
            ? { distributor_pan: panNumber }
            : { distributor_pan: "" };

        const response = await post("/invoice/fetchInvoice", requestBody);

        if (response?.data?.Success) {
          const apiData = response.data.Data[0];

          if (apiData.invoice_details && apiData.invoice_details.length > 0) {
            const transformedInvoices = apiData.invoice_details.map(
              (invoice) => ({
                id: invoice.id,
                distributorName: invoice.distributor_name || "",
                arnCode: invoice.distributor_arn_code || "",
                invoiceNumber: invoice.invoice_number,
                invoiceMonth: invoice.invoice_from_date
                  ? new Date(invoice.invoice_from_date).toLocaleDateString(
                    "en-US",
                    {
                      year: "numeric",
                      month: "long",
                    }
                  )
                  : "",
                amount: `₹${formatIndianNumber(invoice.amount || 0)}`,
                gst: `₹${formatIndianNumber(invoice.gst || 0)}`,
                totalAmount: `₹${formatIndianNumber(
                  invoice.total_amount || 0
                )}`,
                status: getStatusText(invoice.status_id),
                utrNo: invoice.utr_no || "",
                remark: invoice.remark || "",
                rejectionRemark: invoice.rejection_remark || "-",
                invoiceFile: invoice.invoice_file,
                calculationFile: invoice.calculation_file,
                createdDate: new Date(
                  invoice.created_date
                ).toLocaleDateString(),
              })
            );

            setFullData({
              distributor_details: apiData.distributor_details || [],
              invoice_details: transformedInvoices,
            });
          } else {
            setFullData({
              distributor_details: apiData.distributor_details || [],
              invoice_details: [],
            });
          }
        } else {
          toast.info(response?.data?.Message || "No data found");
          setFullData({
            distributor_details: [],
            invoice_details: [],
          });
        }
      } catch (error) {
        toast.error("Failed to fetch invoice data");
        console.error(error);
        setFullData({
          distributor_details: [],
          invoice_details: [],
        });
      } finally {
        setIsLoading(false);
      }
    };

    if (userRole === "distributor" && !panNumber) {
      return;
    }

    fetchInvoiceData();
  }, [userRole, panNumber]);

  const handleEdit = async (invoiceId) => {
    setIsLoading(true);
    try {
      const response = await post("/invoice/fetchInvoiceById", {
        invoice_id: invoiceId,
      });

      if (response?.data?.Success) {
        const invoiceData = response.data.Data[0];
        const invoice = invoiceData.invoice_details[0];

        // Extract file details
        const invoiceFileDetails = invoiceData.invoice_file_details || [];
        const invoiceFile = invoiceFileDetails.find(
          (file) => file.file_type === "invoiceFile"
        );
        const calculationFile = invoiceFileDetails.find(
          (file) => file.file_type === "calculationFile"
        );

        // Set form data for editing
        setFormData({
          distributorName: invoice.distributor_name || "",
          arnCode: invoice.distributor_arn_code || "",
          invoiceNumber: invoice.invoice_number,
          amount: invoice.amount,
          gst: invoice.gst,
          remark: invoice.remark || "",
          invoiceFile: null,
          calculationFile: null,
          invoiceId: invoice.id,
          // Add previous file names and IDs
          previousInvoiceFileName:
            invoiceFile?.file_name || "Previous invoice file",
          previousCalculationFileName:
            calculationFile?.file_name || "Previous calculation file",
          previousInvoiceFileId: invoiceFile?.id || "",
          previousCalculationFileId: calculationFile?.id || "",
        });

        // Set dates
        const fromDateValue = invoice.invoice_from_date
          ? new Date(invoice.invoice_from_date)
          : null;
        setFromDate(fromDateValue);
        setToDate(
          invoice.invoice_to_date ? new Date(invoice.invoice_to_date) : null
        );

        // Set the active month for to date calendar
        if (fromDateValue) {
          setToDateActiveMonth(new Date(fromDateValue));
        }

        // Freeze dates for editing existing invoices
        setDatesFrozen(true);

        // Switch to edit view
        setView("edit");
      } else {
        toast.error(
          response?.data?.Message || "Failed to fetch invoice details"
        );
      }
    } catch (error) {
      toast.error("Error occurred while fetching invoice details");
      console.error(error);
    } finally {
      setIsLoading(false);
    }
  };

  const handleReEdit = async (invoiceId) => {
    // Set the invoice to resubmit and show confirmation modal
    setInvoiceToResubmit(invoiceId);
    setResubmitModalVisible(true);
  };

  const confirmResubmit = async () => {
    if (!invoiceToResubmit || !userDetailsFromStorage.id) {
      toast.error("Missing required information for resubmission");
      return;
    }

    try {
      setIsLoading(true);
      const payload = {
        invoice_id: invoiceToResubmit,
        userId: userDetailsFromStorage.id,
      };

      const response = await post("/invoice/resubmitInvoice", payload);

      if (response?.data?.Success) {
        toast.success("Invoice opened for resubmission successfully");

        // Update the local state to reflect the draft status
        setFullData((prevData) => ({
          ...prevData,
          invoice_details: prevData.invoice_details.map((item) =>
            item.id === invoiceToResubmit
              ? {
                ...item,
                status: "DRAFT", // Update status text
                statusId: 1, // Update status ID to 1 (DRAFT)
              }
              : item
          ),
        }));

        // Close the modal
        setResubmitModalVisible(false);
        setInvoiceToResubmit(null);

        // Navigate to edit view
        handleEdit(invoiceToResubmit);
      } else {
        toast.error(
          response?.data?.Message || "Failed to open invoice for resubmission"
        );
      }
    } catch (error) {
      console.error("Error resubmitting invoice:", error);
      toast.error("Error occurred while opening invoice for resubmission");
    } finally {
      setIsLoading(false);
    }
  };

  // Form handlers
  const handleChange = (e) => {
    const { name, value } = e.target;
    setFormData((prev) => ({ ...prev, [name]: value }));
  };

  const handleReset = () => {
    setFormData({
      distributorName: "",
      arnCode: "",
      invoiceNumber: "",
      amount: "",
      gst: "",
      remark: "",
      invoiceFile: null,
      calculationFile: null,
      invoiceId: "",
      previousInvoiceFileName: "",
      previousCalculationFileName: "",
      previousInvoiceFileId: "",
      previousCalculationFileId: "",
    });
    setFromDate(null);
    setToDate(null);
    setToDateActiveMonth(new Date());
    setDatesFrozen(false);
  };

  // Custom function to check if dates are in the same month
  const areDatesInSameMonth = (date1, date2) => {
    if (!date1 || !date2) return true;

    return (
      date1.getFullYear() === date2.getFullYear() &&
      date1.getMonth() === date2.getMonth()
    );
  };

  const handleSubmit = async () => {
    const {
      invoiceNumber,
      amount,
      gst,
      remark,
      invoiceFile,
      calculationFile,
      arnCode,
      distributorName,
      invoiceId,
    } = formData;

    if (isDistributor && !isDistributorKycReady) {
      toast.error("KYC is pending. Complete KYC before creating an invoice.");
      return;
    }

    if (!invoiceNumber || !amount || !gst || !fromDate || !toDate) {
      toast.error("All fields are required.");
      return;
    }

    if (isDistributor && !areDatesInSameMonth(fromDate, toDate)) {
      toast.error("Invoice From Date and To Date must be in the same month.");
      return;
    }

    if (view === "add" && (!invoiceFile || !calculationFile)) {
      toast.error(
        "Both invoice and calculation files are required for new invoices."
      );
      return;
    }

    if (parseFloat(gst) > parseFloat(amount)) {
      toast.error("GST cannot exceed amount.");
      return;
    }

    if (invoiceFile) {
      const invoiceTypes = [
        "application/pdf",
        "application/vnd.ms-excel",
        "application/vnd.openxmlformats-officedocument.spreadsheetml.sheet",
        "text/csv",
      ];
      if (!invoiceTypes.includes(invoiceFile.type)) {
        toast.error("Invalid invoice file type. Allowed: PDF, Excel, CSV");
        return;
      }
    }

    if (calculationFile) {
      const calcTypes = [
        "application/vnd.ms-excel",
        "application/vnd.openxmlformats-officedocument.spreadsheetml.sheet",
      ];
      if (!calcTypes.includes(calculationFile.type)) {
        toast.error("Invalid calculation file type. Allowed: Excel only");
        return;
      }
    }

    setIsLoading(true);
    try {
      const fd = new FormData();

      // Add all form data as strings
      fd.append("invoice_number", JSON.stringify(invoiceNumber));
      fd.append("amount", amount);
      fd.append("gst_amount", gst);
      fd.append("total_amount", (parseFloat(amount) + parseFloat(gst)).toString());
      fd.append("remark", JSON.stringify(remark));
      fd.append(
        "invoice_from_date",
        JSON.stringify(moment(fromDate).format("YYYY-MM-DD"))
      );
      fd.append(
        "invoice_to_date",
        JSON.stringify(moment(toDate).format("YYYY-MM-DD"))
      );
      fd.append("distributor_arn_code", JSON.stringify(arnCode));
      fd.append("distributor_name", JSON.stringify(distributorName));
      fd.append(
        "userId",
        userId || userDetailsFromStorage.id?.toString() || ""
      );

      // Add invoice ID for update if we're editing
      if (invoiceId) {
        fd.append("invoice_id", invoiceId);
      }

      // Add distributor ID if available
      if (distributorId) {
        fd.append("distributor_id", distributorId);
      }

      // Add files if they exist
      if (invoiceFile) {
        fd.append("invoiceFile", invoiceFile);
      }

      if (calculationFile) {
        fd.append("calculationFile", calculationFile);
      }

      // Determine the API endpoint based on whether we're creating or updating
      const endpoint = invoiceId
        ? "/invoice/updateInvoiceDetails"
        : "/invoice/saveInvoiceDetails";

      const response = await post(endpoint, fd, {
        headers: {
          "Content-Type": "multipart/form-data",
          Authorization: `Bearer ${localStorage.getItem("token")}`,
        },
      });

      if (response?.data?.Success) {
        toast.success(
          invoiceId
            ? "Invoice updated successfully"
            : "Invoice created successfully"
        );

        // Freeze dates after successful submission for new invoices
        if (view === "add") {
          setDatesFrozen(true);
        }

        if (view === "add") {
          handleReset();
          setView("list");
        }

        // Refresh the invoice list
        const requestBody =
          userRole === "distributor"
            ? { distributor_pan: panNumber }
            : { distributor_pan: "" };

        const refreshResponse = await post(
          "/invoice/fetchInvoice",
          requestBody
        );

        if (refreshResponse?.data?.Success) {
          const apiData = refreshResponse.data.Data[0];

          if (apiData.invoice_details && apiData.invoice_details.length > 0) {
            const transformedInvoices = apiData.invoice_details.map(
              (invoice) => ({
                id: invoice.id,
                distributorName: invoice.distributor_name || "",
                arnCode: invoice.distributor_arn_code || "",
                invoiceNumber: invoice.invoice_number,
                invoiceMonth: invoice.invoice_from_date
                  ? new Date(invoice.invoice_from_date).toLocaleDateString(
                    "en-US",
                    {
                      year: "numeric",
                      month: "long",
                    }
                  )
                  : "",
                amount: `₹${formatIndianNumber(invoice.amount || 0)}`,
                gst: `₹${formatIndianNumber(invoice.gst || 0)}`,
                totalAmount: `₹${formatIndianNumber(
                  invoice.total_amount || 0
                )}`,
                status: getStatusText(invoice.status_id),
                utrNo: invoice.utr_no || "",
                remark: invoice.remark || "",
                rejectionRemark: invoice.rejection_remark || "-",
                invoiceFile: invoice.invoice_file,
                calculationFile: invoice.calculation_file,
                createdDate: new Date(
                  invoice.created_date
                ).toLocaleDateString(),
              })
            );

            setFullData({
              distributor_details: apiData.distributor_details || [],
              invoice_details: transformedInvoices,
            });
          }
        }
      } else {
        if (response?.data?.Message === "Mismatch - Amount Or GST") {
          const expectedAmount = response?.data?.Data?.expected_amount || "N/A";
          const expectedGst =
            response?.data?.Data?.expected_gst_amount || "N/A";

          toast.error(
            `Mismatch detected. Expected Amount: ₹${expectedAmount}, Expected GST: ₹${expectedGst}`
          );
        } else {
          toast.error(
            response?.data?.Message ||
            (invoiceId
              ? "Failed to update invoice"
              : "Failed to create invoice")
          );
        }
      }
    } catch (error) {
      console.error("Error processing invoice:", error);

      if (
        error.response?.data?.Message === "Mismatch - Amount Or GST" &&
        error.response?.data?.Data
      ) {
        const expectedAmount =
          error.response.data.Data.expected_amount || "N/A";
        const expectedGst =
          error.response.data.Data.expected_gst_amount || "N/A";

        toast.error(
          `Mismatch detected. Expected Amount: ₹${expectedAmount}, Expected GST: ₹${expectedGst}`
        );
      } else {
        toast.error("Error occurred while processing invoice");
      }
    } finally {
      setIsLoading(false);
    }
  };

  const handleApprove = async () => {
    if (!selectedInvoice?.id || !userDetailsFromStorage.id) {
      toast.error("Missing required information for approval");
      return;
    }

    try {
      setIsLoading(true);
      const payload = {
        invoice_id: selectedInvoice.id,
        userId: userDetailsFromStorage.id,
      };

      const response = await post("/invoice/approveInvoice", payload);

      if (response?.data?.Success) {
        toast.success("Invoice approved successfully");

        // Update the local state to reflect the approved status
        setFullData((prevData) => ({
          ...prevData,
          invoice_details: prevData.invoice_details.map((item) =>
            item.id === selectedInvoice.id
              ? {
                ...item,
                status: "Approved", // Update status text
                statusId: 3, // Update status ID to 3 (Approved)
              }
              : item
          ),
        }));

        // Also update the selected invoice if it's currently being viewed
        setSelectedInvoice((prev) =>
          prev
            ? {
              ...prev,
              status: "Approved",
              statusId: 3,
            }
            : null
        );

        // Navigate back to list view
        setView("list");
      } else {
        toast.error(response?.data?.Message || "Failed to approve invoice");
      }
    } catch (error) {
      console.error("Error approving invoice:", error);
      toast.error("Error occurred while approving invoice");
    } finally {
      setIsLoading(false);
    }
  };

  const handleReject = async () => {
    if (!rejectionRemark.trim()) {
      toast.error("Please provide a rejection remark");
      return;
    }

    if (!selectedInvoice?.id || !userDetailsFromStorage.id) {
      toast.error("Missing required information for rejection");
      return;
    }

    try {
      setIsLoading(true);
      const payload = {
        invoice_id: selectedInvoice.id,
        userId: userDetailsFromStorage.id,
        rejection_remark: rejectionRemark,
      };

      const response = await post("/invoice/rejectInvoice", payload);

      if (response?.data?.Success) {
        toast.success("Invoice rejected successfully");

        // Update the local state to reflect the rejected status
        setFullData((prevData) => ({
          ...prevData,
          invoice_details: prevData.invoice_details.map((item) =>
            item.id === selectedInvoice.id
              ? {
                ...item,
                status: "Rejected",
                statusId: 4, // Update status ID to 4 (Rejected)
                rejectionRemark: rejectionRemark,
              }
              : item
          ),
        }));

        // Close the modal and reset the rejection remark
        setRejectModalVisible(false);
        setRejectionRemark("");

        // Navigate back to list view
        setView("list");
      } else {
        toast.error(response?.data?.Message || "Failed to reject invoice");
      }
    } catch (error) {
      console.error("Error rejecting invoice:", error);
      toast.error("Error occurred while rejecting invoice");
    } finally {
      setIsLoading(false);
    }
  };

  const isFromDateTileDisabled = ({ date, view }) => {
    if (view !== "month" && view !== "year") {
      const today = new Date();
      today.setHours(0, 0, 0, 0);
      const targetDate = new Date(date);
      targetDate.setHours(0, 0, 0, 0);
      return targetDate > today;
    }
    return false;
  };

  const isToDateTileDisabled = ({ date, view }) => {
    if (view !== "month" && view !== "year") {
      const today = new Date();
      today.setHours(0, 0, 0, 0);
      const targetDate = new Date(date);
      targetDate.setHours(0, 0, 0, 0);

      // Disable future dates
      if (targetDate > today) {
        return true;
      }

      // If distributor, restrict to same month as fromDate
      if (isDistributor && fromDate) {
        return !areDatesInSameMonth(fromDate, targetDate);
      }
    }
    return false;
  };

  // New function to fetch expected amount and GST
  const fetchExpectedAmountAndGst = async (fromDate, toDate, arnCode, pan) => {
    if (!fromDate || !toDate) return;

    try {
      setIsLoading(true);
      const response = await post("/invoice/fetchExpectedAmountGst", {
        distributor_arn_code: arnCode || '',
        distributor_pan: pan || '',
        invoice_from_date: moment(fromDate).format("YYYY-MM-DD"),
        invoice_to_date: moment(toDate).format("YYYY-MM-DD"),
      });

      if (response?.data?.Success && response.data.Data) {
        setFormData((prev) => ({
          ...prev,
          amount: response.data.Data.expected_amount || "",
          gst: response.data.Data.expected_gst_amount || "",
        }));
      } else {
        setFormData((prev) => ({
          ...prev,
          amount: "",
          gst: "",
        }));
        toast.error(response?.data?.Message || "No data found for selected dates");
      }
    } catch (error) {
      setFormData((prev) => ({
        ...prev,
        amount: "",
        gst: "",
      }));
      toast.error("Failed to fetch expected amount and GST");
    } finally {
      setIsLoading(false);
    }
  };

  const statusItemTemplate = (option) => {
    const status = option.status_desc;
    let badgeClass = "";

    switch (status) {
      case "APPROVED":
        badgeClass = "bg-emerald-50 text-emerald-700 border-emerald-200";
        break;
      case "REJECTED":
        badgeClass = "bg-red-50 text-red-700 border-red-200";
        break;
      case "PENDING FOR APPROVAL":
        badgeClass = "bg-amber-50 text-amber-700 border-amber-200";
        break;
      case "DRAFT":
        badgeClass = "bg-blue-50 text-blue-700 border-blue-200";
        break;
      default:
        badgeClass = "bg-gray-50 text-gray-700 border-gray-200";
    }

    return (
      <div className="flex items-center gap-2">
        <span className={`px-3 py-1 rounded-full text-[10px] font-bold uppercase tracking-wider border ${badgeClass}`}>
          {status}
        </span>
      </div>
    );
  };

  const selectedStatusTemplate = (option, props) => {
    if (option) {
      return statusItemTemplate(option);
    }
    return <span>{props.placeholder}</span>;
  };

  return (
    <div className="max-w-screen-xl mx-auto p-6 space-y-6">
      <div className="flex justify-between items-center">
        <div className="flex flex-col md:flex-row gap-4 w-full md:w-auto">
          <Dropdown
            value={statusFilter}
            onChange={(e) => setStatusFilter(e.value)}
            options={[{ id: '', status_desc: 'All Status' }, ...statusOptions]}
            optionLabel="status_desc"
            optionValue="status_desc"
            placeholder="Select Status"
            className="w-full md:w-[260px] rounded-xl border-gray-200 shadow-sm focus:ring-2 focus:ring-indigo-500 transition-all duration-300"
            valueTemplate={selectedStatusTemplate}
            itemTemplate={statusItemTemplate}
            pt={{
              root: { className: 'h-[46px] flex items-center bg-white border-2 border-indigo-50/50 hover:border-indigo-200 transition-colors' },
              input: { className: 'px-4 py-0 text-gray-700 font-semibold' },
              trigger: { className: 'pr-4 text-indigo-400' },
              panel: { className: 'rounded-2xl shadow-2xl border-none mt-3 overflow-hidden animate-in fade-in zoom-in duration-200' },
              list: { className: 'p-2 space-y-1 bg-white' },
              item: { className: 'rounded-xl px-2 py-2 hover:bg-indigo-50/50 transition-all duration-200 cursor-pointer' },
              header: { className: 'hidden' }
            }}
          />
        </div>
        <div className="flex items-center gap-3">
          {view === "list" ? (
            isMaker && (
              <Button
                variant="default"
                onClick={() => setView("add")}
                className="bg-indigo-600 hover:bg-indigo-700 text-white px-6 py-2 rounded-lg shadow-lg shadow-indigo-200 transition-all duration-300 flex items-center gap-2 transform hover:-translate-y-0.5"
              >
                <span className="text-xl font-light">+</span> Add Invoice
              </Button>
            )
          ) : (
            <Button
              variant="outline"
              onClick={() => {
                setView("list");
                setSelectedInvoice(null);
                setRejectionRemark("");
                handleReset();
              }}
              className="border-gray-100 hover:bg-gray-50 hover:text-black text-gray-600 px-5 py-2 rounded-full transition-all duration-300 flex items-center gap-2 shadow-sm text-sm font-medium"
            >
              <ArrowBigLeft className="w-4 h-4" />
              Back to List
            </Button>
          )}
        </div>
      </div>

      {/* List View */}
      {
        view === "list" && (
          <div className="bg-white rounded-xl shadow-sm border border-gray-100 overflow-hidden">
            <div className="p-0">
              {isLoading ? (
                <div className="flex flex-col items-center justify-center py-20 gap-4">
                  <div className="w-12 h-12 border-4 border-indigo-200 border-t-indigo-600 rounded-full animate-spin"></div>
                  <p className="text-gray-500 font-medium animate-pulse">Loading invoices...</p>
                </div>
              ) : (
                <div className="overflow-x-auto">
                  <ReusableTable
                    columns={columns}
                    data={filteredData}
                    emptyMessage={`No invoices found${userRole === "distributor" ? " for your account" : ""}`}
                    className="w-full"
                  />
                </div>
              )}
            </div>
          </div>
        )
      }

      {/* View Invoice Details */}
      {
        view === "view" && selectedInvoice && (
          <div className="bg-white rounded-3xl shadow-2xl border border-gray-100 overflow-hidden transition-all duration-500 max-w-4xl mx-auto mt-6">
            <div className="p-8 bg-gradient-to-r from-indigo-600 to-indigo-800 text-white flex justify-between items-start">
              <div>
                <p className="text-indigo-100 text-xs font-bold uppercase tracking-widest mb-1">Invoice Details</p>
                <h2 className="text-3xl font-extrabold tracking-tight">{selectedInvoice.invoiceNumber}</h2>
              </div>
              <div className="flex flex-col items-end">
                <span className={`px-4 py-1.5 rounded-full text-xs font-bold uppercase tracking-wider ${selectedInvoice.statusId === 3 ? "bg-emerald-400/20 text-emerald-100 border border-emerald-400/30" :
                  selectedInvoice.statusId === 4 ? "bg-red-400/20 text-red-100 border border-red-400/30" :
                    "bg-indigo-400/20 text-indigo-100 border border-indigo-400/30"
                  }`}>
                  {selectedInvoice.status}
                </span>
                <p className="mt-2 text-indigo-200 text-xs font-medium italic">{selectedInvoice.invoiceMonth}</p>
              </div>
            </div>

            <div className="p-10 space-y-10">
              {/* Main Info Grid */}
              <div className="grid grid-cols-1 md:grid-cols-2 gap-x-12 gap-y-4">
                <div className="space-y-6">
                  <div className="group">
                    <label className="block text-[10px] font-bold text-gray-400 uppercase tracking-widest mb-1 transition-colors group-hover:text-indigo-600">Distributor Name</label>
                    <p className="text-lg font-bold text-gray-800 border-b border-gray-100 pb-2 group-hover:border-indigo-100 transition-colors uppercase">{selectedInvoice.distributorName}</p>
                  </div>
                  <div className="group">
                    <label className="block text-[10px] font-bold text-gray-400 uppercase tracking-widest mb-1 transition-colors group-hover:text-indigo-600">APRN Code</label>
                    <p className="text-lg font-bold text-gray-800 border-b border-gray-100 pb-2 group-hover:border-indigo-100 transition-colors uppercase">{selectedInvoice.arnCode}</p>
                  </div>
                </div>

                <div className="space-y-6">
                  <div className="grid grid-cols-2 gap-4">
                    <div className="group">
                      <label className="block text-[10px] font-bold text-gray-400 uppercase tracking-widest mb-1 transition-colors group-hover:text-indigo-600">From Date</label>
                      <p className="text-sm font-bold text-gray-700 bg-gray-50 px-3 py-2 rounded-lg border border-gray-100 group-hover:bg-indigo-50/30 transition-colors">{selectedInvoice.invoiceFromDate}</p>
                    </div>
                    <div className="group">
                      <label className="block text-[10px] font-bold text-gray-400 uppercase tracking-widest mb-1 transition-colors group-hover:text-indigo-600">To Date</label>
                      <p className="text-sm font-bold text-gray-700 bg-gray-50 px-3 py-2 rounded-lg border border-gray-100 group-hover:bg-indigo-50/30 transition-colors">{selectedInvoice.invoiceToDate}</p>
                    </div>
                  </div>
                  <div className="group">
                    <label className="block text-[10px] font-bold text-gray-400 uppercase tracking-widest mb-1 transition-colors group-hover:text-indigo-600 text-right">UTR Number</label>
                    <p className="text-lg font-bold text-gray-800 text-right border-b border-gray-100 pb-2 group-hover:border-indigo-100 transition-colors">{selectedInvoice.utrNo || "---"}</p>
                  </div>
                </div>
              </div>

              {/* Financial Summary (Receipt Style) */}
              <div className="bg-gray-50 rounded-3xl p-8 border border-gray-100 shadow-inner relative overflow-hidden">
                <div className="absolute top-0 right-0 w-32 h-32 bg-indigo-600/5 rounded-full -mr-16 -mt-16 pointer-events-none"></div>
                <div className="space-y-4 relative z-10">
                  <div className="flex justify-between items-center text-gray-600 font-medium">
                    <span>Base Amount</span>
                    <span className="font-bold text-gray-800">{selectedInvoice.amount}</span>
                  </div>
                  <div className="flex justify-between items-center text-gray-600 font-medium">
                    <span>GST Amount</span>
                    <span className="font-bold text-gray-800">{selectedInvoice.gst}</span>
                  </div>
                  <div className="pt-4 border-t-2 border-dashed border-gray-200 flex justify-between items-center">
                    <div>
                      <span className="text-sm font-bold text-indigo-600 uppercase tracking-wider block">Total Payable</span>
                      <span className="text-[10px] text-gray-400 font-medium italic max-w-[200px] block">
                        {numberToWords(parseFloat(selectedInvoice.totalAmount.replace("₹", "").replace(/,/g, "")) || 0)}
                      </span>
                    </div>
                    <span className="text-3xl font-black text-indigo-700 tracking-tight">{selectedInvoice.totalAmount}</span>
                  </div>
                </div>
              </div>

              {/* Remark Section */}
              <div className="space-y-4">
                <div className="group">
                  <label className="block text-[10px] font-bold text-gray-400 uppercase tracking-widest mb-2 transition-colors group-hover:text-indigo-600">Maker Remark</label>
                  <div className="p-4 bg-gray-50 rounded-2xl border border-gray-100 italic text-gray-600 text-sm group-hover:bg-white group-hover:shadow-sm transition-all min-h-[60px]">
                    {selectedInvoice.remark || "No remarks provided"}
                  </div>
                </div>

                {selectedInvoice.statusId === 4 && (
                  <div className="group animate-pulse">
                    <label className="block text-[10px] font-bold text-red-500 uppercase tracking-widest mb-2">Rejection Reason</label>
                    <div className="p-4 bg-red-50 rounded-2xl border border-red-100 italic text-red-700 text-sm group-hover:shadow-md transition-all">
                      {selectedInvoice.rejectionRemark}
                    </div>
                  </div>
                )}
              </div>

              {/* Downloads Section */}
              <div className="grid grid-cols-1 md:grid-cols-2 gap-6 pt-4">
                <div className={`p-6 rounded-2xl border group transition-all duration-300 ${selectedInvoice.invoiceFileId ? "bg-white border-gray-100 shadow-sm hover:shadow-md hover:border-indigo-200" : "bg-gray-50 border-transparent opacity-60"}`}>
                  <div className="flex items-center justify-between mb-4">
                    <div className="flex items-center gap-3">
                      <div className={`w-10 h-10 rounded-full flex items-center justify-center ${selectedInvoice.invoiceFileId ? "bg-indigo-50 text-indigo-600" : "bg-gray-200 text-gray-400"}`}>
                        <Download className="w-5 h-5 rotate-180" />
                      </div>
                      <div>
                        <p className="text-xs font-bold text-gray-700 uppercase tracking-tight">Invoice File</p>
                        <p className="text-[10px] text-gray-400 font-medium max-w-[150px] truncate">{selectedInvoice.invoiceFileName || "Not Available"}</p>
                      </div>
                    </div>
                    {selectedInvoice.invoiceFileId && (
                      <button
                        onClick={() => downloadFile(selectedInvoice.invoiceFileId, selectedInvoice.invoiceFileName)}
                        className="bg-indigo-600 text-white p-2 rounded-lg hover:bg-indigo-700 shadow-lg shadow-indigo-100 transition-all active:scale-90"
                      >
                        <Download className="w-4 h-4" />
                      </button>
                    )}
                  </div>
                </div>

                <div className={`p-6 rounded-2xl border group transition-all duration-300 ${selectedInvoice.calculationFileId ? "bg-white border-gray-100 shadow-sm hover:shadow-md hover:border-emerald-200" : "bg-gray-50 border-transparent opacity-60"}`}>
                  <div className="flex items-center justify-between mb-4">
                    <div className="flex items-center gap-3">
                      <div className={`w-10 h-10 rounded-full flex items-center justify-center ${selectedInvoice.calculationFileId ? "bg-emerald-50 text-emerald-600" : "bg-gray-200 text-gray-400"}`}>
                        <Download className="w-5 h-5 rotate-180" />
                      </div>
                      <div>
                        <p className="text-xs font-bold text-gray-700 uppercase tracking-tight">Calculation File</p>
                        <p className="text-[10px] text-gray-400 font-medium max-w-[150px] truncate">{selectedInvoice.calculationFileName || "Not Available"}</p>
                      </div>
                    </div>
                    {selectedInvoice.calculationFileId && (
                      <button
                        onClick={() => downloadFile(selectedInvoice.calculationFileId, selectedInvoice.calculationFileName)}
                        className="bg-emerald-600 text-white p-2 rounded-lg hover:bg-emerald-700 shadow-lg shadow-emerald-100 transition-all active:scale-90"
                      >
                        <Download className="w-4 h-4" />
                      </button>
                    )}
                  </div>
                </div>
              </div>
            </div>

            {isChecker && selectedInvoice.statusId === 2 && (
              <div className="p-8 bg-gray-50 border-t border-gray-100 flex justify-end items-center gap-4">
                <Button
                  variant="outline"
                  onClick={() => setRejectModalVisible(true)}
                  disabled={isLoading}
                  className="px-10 py-3 rounded-xl border-red-200 text-red-600 font-bold hover:bg-red-50 transition-all"
                >
                  Reject Invoice
                </Button>
                <Button
                  variant="default"
                  onClick={handleApprove}
                  disabled={isLoading}
                  className="px-10 py-3 rounded-xl bg-emerald-600 text-white font-bold hover:bg-emerald-700 shadow-lg shadow-emerald-100 transition-all transform hover:-translate-y-0.5 flex items-center gap-2"
                >
                  {isLoading ? "Processing..." : (
                    <>
                      <CheckCircle2 className="w-5 h-5" />
                      Approve Invoice
                    </>
                  )}
                </Button>
              </div>
            )}
          </div>
        )
      }

      {/* Add/Edit Invoice Form */}
      {
        (view === "add" || view === "edit") && (
          <div className="bg-white rounded-xl shadow-sm border border-gray-100 overflow-hidden mt-6 max-w-5xl mx-auto">
            <div className="p-4 text-center border-b border-gray-50">
              <h2 className="text-2xl font-extrabold text-gray-900 tracking-tight">
                {view === "add" ? "Add Invoice" : "Edit Invoice Details"}
              </h2>
            </div>

            {view === "add" && isDistributor && !isDistributorKycReady ? (
              <div className="p-10">
                <div className="rounded-2xl border border-amber-200 bg-amber-50 px-6 py-8 text-center shadow-sm">
                  <p className="text-xs font-bold uppercase tracking-[0.2em] text-amber-700">
                    KYC Pending
                  </p>
                  <h3 className="mt-2 text-2xl font-extrabold text-gray-900">
                    Complete KYC to create an invoice
                  </h3>
                  <p className="mt-3 text-sm font-medium text-gray-600">
                    Your current KYC status is{" "}
                    <span className="font-bold uppercase text-amber-700">
                      {distributorKycStatus || "Pending"}
                    </span>
                    .{" "}
                    <button
                      type="button"
                      onClick={() => navigate("/onboarding_listing?tab=form")}
                      className="font-bold text-indigo-600 underline underline-offset-2 transition-colors hover:text-indigo-800"
                    >
                      Complete KYC
                    </button>{" "}
                    to create the invoice.
                  </p>
                  <button
                    type="button"
                    onClick={() => navigate("/onboarding_listing?tab=form")}
                    className="mt-6 inline-flex items-center rounded-xl bg-indigo-600 px-5 py-3 text-sm font-bold text-white shadow-lg shadow-indigo-100 transition-all duration-300 hover:bg-indigo-700"
                  >
                    Go To KYC Screen
                  </button>
                </div>
              </div>
            ) : (
            <div className="p-10 space-y-4">
              {/* Row 1: Distributor Info & Invoice No */}
              <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
                <div className="space-y-1">
                  <label className="block text-sm font-semibold text-gray-600 ml-1">Distributor Name</label>
                  <InputText
                    value={formData.distributorName}
                    readOnly
                    placeholder="Enter distributor name"
                    className="w-full px-4 py-3 bg-gray-50/50 border-gray-100 rounded-xl text-gray-700 font-medium focus:ring-2 focus:ring-indigo-500 transition-all shadow-sm"
                  />
                </div>
                <div className="space-y-1">
                  <label className="block text-sm font-semibold text-gray-600 ml-1">APRN Code</label>
                  <InputText
                    value={formData.arnCode}
                    readOnly
                    placeholder="Enter APRN code"
                    className="w-full px-4 py-3 bg-gray-50/50 border-gray-100 rounded-xl text-gray-700 font-medium focus:ring-2 focus:ring-indigo-500 transition-all shadow-sm"
                  />
                </div>
                <div className="space-y-1">
                  <label className="block text-sm font-semibold text-gray-600 ml-1">Invoice Number</label>
                  <InputText
                    name="invoiceNumber"
                    value={formData.invoiceNumber}
                    onChange={handleChange}
                    placeholder="Enter invoice number"
                    className="w-full px-4 py-3 bg-gray-50/50 border-gray-100 rounded-xl text-gray-700 font-medium focus:ring-2 focus:ring-indigo-500 transition-all shadow-sm"
                  />
                </div>
              </div>

              {/* Row 2: Dates & Amount */}
              <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
                <div className="relative space-y-1">
                  <label className="block text-sm font-semibold text-gray-600 ml-1">Invoice From Date</label>
                  <div className="relative">
                    <InputText
                      readOnly={datesFrozen}
                      value={fromDate ? fromDate.toLocaleDateString() : ""}
                      onClick={() => !datesFrozen && setFromCalendarOpen(true)}
                      ref={inputRef}
                      placeholder="Select date"
                      className={`w-full px-4 py-3 bg-gray-50/50 border-gray-100 rounded-xl text-gray-700 font-medium focus:ring-2 focus:ring-indigo-500 transition-all shadow-sm ${datesFrozen ? "cursor-not-allowed" : "cursor-pointer"}`}
                    />
                    <div className="absolute right-4 top-1/2 -translate-y-1/2 text-gray-400 pointer-events-none">
                      <CalendarIcon className="w-5 h-5" />
                    </div>
                  </div>
                  {fromCalendarOpen && !datesFrozen && (
                    <div ref={calendarRef} className="absolute z-20 bg-white shadow-2xl mt-2 rounded-2xl border border-gray-100 p-3 transform scale-95 origin-top-left">
                      <ReactCalendar
                        onChange={(date) => {
                          const today = new Date();
                          today.setHours(0, 0, 0, 0);
                          date.setHours(0, 0, 0, 0);
                          if (date > today) { toast.error("Cannot select a future date"); return; }
                          setFromDate(date);
                          setFromCalendarOpen(false);
                          setToDateActiveMonth(new Date(date));
                          if (date && toDate) {
                            fetchExpectedAmountAndGst(date, toDate, formData.arnCode, panNumber);
                          }
                        }}
                        value={fromDate}
                        tileDisabled={isFromDateTileDisabled}
                        maxDate={new Date()}
                      />
                    </div>
                  )}
                </div>

                <div className="relative space-y-1">
                  <label className="block text-sm font-semibold text-gray-600 ml-1">Invoice To Date</label>
                  <div className="relative">
                    <InputText
                      readOnly={datesFrozen}
                      value={toDate ? toDate.toLocaleDateString() : ""}
                      onClick={() => !datesFrozen && setToCalendarOpen(true)}
                      ref={toInputRef}
                      placeholder="Select date"
                      className={`w-full px-4 py-3 bg-gray-50/50 border-gray-100 rounded-xl text-gray-700 font-medium focus:ring-2 focus:ring-indigo-500 transition-all shadow-sm ${datesFrozen ? "cursor-not-allowed" : "cursor-pointer"}`}
                    />
                    <div className="absolute right-4 top-1/2 -translate-y-1/2 text-gray-400 pointer-events-none">
                      <CalendarIcon className="w-5 h-5" />
                    </div>
                  </div>
                  {toCalendarOpen && !datesFrozen && (
                    <div ref={calendarRef} className="absolute z-20 bg-white shadow-2xl mt-2 rounded-2xl border border-gray-100 p-3 transform scale-95 origin-top-left">
                      <ReactCalendar
                        onChange={(date) => {
                          const today = new Date();
                          today.setHours(0, 0, 0, 0);
                          date.setHours(0, 0, 0, 0);
                          if (date > today) { toast.error("Cannot select a future date"); return; }
                          if (isDistributor && fromDate && !areDatesInSameMonth(fromDate, date)) {
                            toast.error("To Date must be in the same month as From Date");
                            return;
                          }
                          setToDate(date);
                          setToCalendarOpen(false);
                          if (fromDate && date) {
                            fetchExpectedAmountAndGst(fromDate, date, formData.arnCode, panNumber);
                          }
                        }}
                        value={toDate}
                        activeStartDate={toDateActiveMonth}
                        tileDisabled={isToDateTileDisabled}
                        maxDate={new Date()}
                      />
                    </div>
                  )}
                </div>

                <div className="space-y-1">
                  <label className="block text-sm font-semibold text-gray-600 ml-1">Amount</label>
                  <InputText
                    name="amount"
                    value={formData.amount}
                    onChange={(e) => {
                      const val = e.target.value;
                      const regex = /^\d*\.?\d{0,2}$/;
                      if (regex.test(val) || val === "") {
                        setFormData((prev) => ({ ...prev, amount: val }));
                      }
                    }}
                    className="w-full px-4 py-3 bg-gray-50/50 border-gray-100 rounded-xl text-gray-700 font-medium focus:ring-2 focus:ring-indigo-500 transition-all shadow-sm"
                    placeholder="0.00"
                  />
                  <p className="text-[11px] text-gray-400 font-medium ml-1">
                    {numberToWords(parseFloat(formData.amount || 0))}
                  </p>
                </div>
              </div>

              {/* Row 3: GST & Total Amount */}
              <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
                <div className="space-y-1">
                  <label className="block text-sm font-semibold text-gray-600 ml-1">GST</label>
                  <InputText
                    name="gst"
                    value={formData.gst}
                    onChange={(e) => {
                      const val = e.target.value;
                      const regex = /^\d*\.?\d{0,2}$/;
                      if (regex.test(val) || val === "") {
                        setFormData((prev) => ({ ...prev, gst: val }));
                      }
                    }}
                    className="w-full px-4 py-3 bg-gray-50/50 border-gray-100 rounded-xl text-gray-700 font-medium focus:ring-2 focus:ring-indigo-500 transition-all shadow-sm"
                    placeholder="0.00"
                  />
                  <p className="text-[11px] text-gray-400 font-medium ml-1">
                    {numberToWords(parseFloat(formData.gst || 0))}
                  </p>
                </div>
                <div className="space-y-1">
                  <label className="block text-sm font-semibold text-gray-600 ml-1">Total Amount</label>
                  <InputText
                    value={(parseFloat(formData.amount || 0) + parseFloat(formData.gst || 0)).toFixed(2)}
                    readOnly
                    className="w-full px-4 py-3 bg-gray-50/50 border-gray-100 rounded-xl text-gray-700 font-bold focus:ring-0 shadow-sm"
                    placeholder="0.00"
                  />
                  <p className="text-[11px] text-gray-400 font-medium ml-1">
                    {numberToWords(parseFloat(formData.amount || 0) + parseFloat(formData.gst || 0))}
                  </p>
                </div>
              </div>

              {/* Row 4: Remark */}
              <div className="space-y-1">
                <label className="block text-sm font-semibold text-gray-600 ml-1">Remark</label>
                <InputTextarea
                  name="remark"
                  value={formData.remark}
                  onChange={handleChange}
                  rows={3}
                  placeholder="Enter remarks"
                  className="w-full px-4 py-3 bg-gray-50/50 border-gray-100 rounded-2xl text-gray-700 font-medium focus:ring-2 focus:ring-indigo-500 transition-all shadow-sm"
                />
              </div>

              {/* Row 5: Uploads */}
              <div className="grid grid-cols-1 md:grid-cols-2 gap-8">
                <div className="space-y-1">
                  <label className="block text-sm font-semibold text-gray-600 ml-1">Invoice Upload</label>
                  <div className="relative group">
                    <input
                      type="file"
                      id="invoice-upload"
                      hidden
                      accept=".pdf,.xls,.xlsx,.csv"
                      onChange={(e) => setFormData((prev) => ({ ...prev, invoiceFile: e.target.files[0] }))}
                    />
                    <label
                      htmlFor="invoice-upload"
                      className="flex items-center justify-center w-full px-6 py-8 bg-gray-50/50 border-2 border-dashed border-gray-200 rounded-2xl cursor-pointer hover:bg-white hover:border-indigo-400 transition-all group-hover:shadow-md"
                    >
                      <span className="text-gray-500 font-medium truncate max-w-full px-4 text-center">
                        {formData.invoiceFile ? formData.invoiceFile.name : "Select File"}
                      </span>
                    </label>
                    {view === "edit" && formData.previousInvoiceFileName && !formData.invoiceFile && (
                      <div className="mt-2 flex items-center justify-between bg-indigo-50/30 p-2 rounded-lg border border-indigo-100">
                        <span className="text-[10px] text-indigo-600 font-medium truncate max-w-[200px]">Current: {formData.previousInvoiceFileName}</span>
                        <button onClick={() => downloadFile(formData.previousInvoiceFileId, formData.previousInvoiceFileName)} className="text-indigo-600 hover:text-indigo-800"><Download className="w-3 h-3" /></button>
                      </div>
                    )}
                  </div>
                </div>

                <div className="space-y-1">
                  <label className="block text-sm font-semibold text-gray-600 ml-1">Calculation File</label>
                  <div className="relative group">
                    <input
                      type="file"
                      id="calc-upload"
                      hidden
                      accept=".xls,.xlsx"
                      onChange={(e) => setFormData((prev) => ({ ...prev, calculationFile: e.target.files[0] }))}
                    />
                    <label
                      htmlFor="calc-upload"
                      className="flex items-center justify-center w-full px-6 py-8 bg-gray-50/50 border-2 border-dashed border-gray-200 rounded-2xl cursor-pointer hover:bg-white hover:border-indigo-400 transition-all group-hover:shadow-md"
                    >
                      <span className="text-gray-500 font-medium truncate max-w-full px-4 text-center">
                        {formData.calculationFile ? formData.calculationFile.name : "Select File"}
                      </span>
                    </label>
                    {view === "edit" && formData.previousCalculationFileName && !formData.calculationFile && (
                      <div className="mt-2 flex items-center justify-between bg-emerald-50/30 p-2 rounded-lg border border-emerald-100">
                        <span className="text-[10px] text-emerald-600 font-medium truncate max-w-[200px]">Current: {formData.previousCalculationFileName}</span>
                        <button onClick={() => downloadFile(formData.previousCalculationFileId, formData.previousCalculationFileName)} className="text-emerald-600 hover:text-emerald-800"><Download className="w-3 h-3" /></button>
                      </div>
                    )}
                  </div>
                </div>
              </div>

              {/* Action Buttons */}
              <div className="pt-6 flex justify-end gap-6 items-center">
                <Button
                  variant="outline"
                  onClick={handleReset}
                  className="px-10 py-3 rounded-xl border-gray-200 text-gray-500 font-bold hover:bg-gray-50 transition-all"
                >
                  Reset
                </Button>
                <Button
                  variant="default"
                  onClick={handleSubmit}
                  className="px-12 py-3 rounded-xl bg-indigo-600 text-white font-bold hover:bg-indigo-700 shadow-xl shadow-indigo-100 transition-all transform hover:-translate-y-0.5"
                >
                  Submit
                </Button>
              </div>
            </div>
            )}
          </div>
        )
      }

      {/* Rejection Modal */}
      <CustomDialog
        visible={rejectModalVisible}
        onHide={() => setRejectModalVisible(false)}
        header="Reject Invoice"
        width="50vw"
      >
        <div className="space-y-4">
          <p className="text-gray-700">
            Please provide a reason for rejecting this invoice:
          </p>
          <InputTextarea
            value={rejectionRemark}
            onChange={(e) => setRejectionRemark(e.target.value)}
            rows={4}
            className="w-full border-gray-300 rounded-md px-2"
            placeholder="Enter rejection reason..."
          />
          <div className="flex justify-end gap-3 mt-4">
            <Button
              variant="cancel"
              onClick={() => {
                setRejectModalVisible(false);
                setRejectionRemark("");
              }}
            >
              Cancel
            </Button>
            <Button
              variant="default"
              onClick={handleReject}
              disabled={isLoading || !rejectionRemark.trim()}
            >
              {isLoading ? "Processing..." : "Confirm Reject"}
            </Button>
          </div>
        </div>
      </CustomDialog>

      {/* Resubmit Confirmation Modal */}
      <CustomDialog
        visible={resubmitModalVisible}
        onHide={() => {
          setResubmitModalVisible(false);
          setInvoiceToResubmit(null);
        }}
        header="Confirm Resubmission"
        width="40vw"
      >
        <div className="space-y-4">
          <p className="text-gray-700">
            Are you sure you want to open this invoice for resubmission? This
            will change the status back to DRAFT and allow you to edit all
            fields.
          </p>
          <div className="flex justify-end gap-3 mt-4">
            <Button
              variant="cancel"
              onClick={() => {
                setResubmitModalVisible(false);
                setInvoiceToResubmit(null);
              }}
            >
              Cancel
            </Button>
            <Button
              variant="default"
              onClick={confirmResubmit}
              disabled={isLoading}
            >
              {isLoading ? "Processing..." : "Confirm Resubmit"}
            </Button>
          </div>
        </div>
      </CustomDialog>

      <ToastContainer position="top-right" autoClose={3000} />
    </div>
  );
};

export default Invoice;
